import { useEffect, useCallback, useRef, type ReactNode } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faExclamationCircle,
  faExclamationTriangle,
  faCheck,
  faInfoCircle,
  faTimes,
} from '@fortawesome/pro-regular-svg-icons';

import cx from '@/lib/cx';

const icons = {
  error: faExclamationCircle,
  warning: faExclamationTriangle,
  success: faCheck,
  info: faInfoCircle,
};

const durations = {
  short: 4000,
  long: 8000,
};

type ToastProps = {
  onDismiss: VoidFunction;
  type?: 'error' | 'warning' | 'success' | 'info';
  duration?: 'short' | 'long';
  message?: ReactNode;
};

const Toast = ({ onDismiss, duration, type = 'info', message }: ToastProps) => {
  const timer = useRef(null);

  const setTimer = useCallback(() => {
    if (!duration) return;
    timer.current = setTimeout(onDismiss, durations[duration]);
  }, [timer, duration, onDismiss]);

  const clearTimer = useCallback(() => {
    clearTimeout(timer.current);
    timer.current = null;
  }, [timer]);

  useEffect(() => {
    setTimer();
    return () => clearTimer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      className="px-4 py-5 bg-gray-900 rounded-lg shadow-md text-gray-200 relative"
      onMouseEnter={clearTimer}
      onMouseLeave={setTimer}
    >
      <div className="flex items-start">
        <div className="shrink-0">
          <FontAwesomeIcon
            icon={icons[type]}
            size="lg"
            fixedWidth
            className={cx({
              'text-green-500': type === 'success',
              'text-red-500': type === 'error',
              'text-orange-400': type === 'warning',
              'text-gray-600': type === 'info',
            })}
          />
        </div>
        <div className="grow pl-3">{message}</div>
        <div className="hidden sm:block pl-4 shrink-0">
          <button
            type="button"
            className="text-gray-600 hover:text-gray-400 transition-colors duration-200 before:absolute before:inset-0 before:bg-green-500/50 sm:before:hidden"
            onClick={onDismiss}
          >
            <FontAwesomeIcon icon={faTimes} size="lg" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default Toast;
